export interface VideoStyle {
  id: string;
  nameKey: string;
  descriptionKey: string;
  enabled: boolean;
  badgeKey: string | null;
  estimated_time: string | null;
  thumbnail?: string;
  thumbnailType?: "image" | "video";
}

export interface VideoTypeOption {
  id: string;
  nameKey: string;
  descriptionKey: string;
  badge: string;
  icon: "package" | "search";
  thumbnail?: string;
}

export const VIDEO_TYPE_OPTIONS: VideoTypeOption[] = [];

export interface UGCAvatarConfig {
  gender: "female" | "male";
  age_range: "18-25" | "25-35" | "35-45" | "45+";
  skin_tone: "light" | "medium" | "tan" | "dark";
  hair: "short-straight" | "short-curly" | "long-straight" | "long-wavy" | "long-curly" | "bun" | "ponytail";
  hair_color: "black" | "brown" | "blonde" | "red" | "gray";
  vibe: "casual-natural" | "professional" | "sporty" | "glam";
  setting: "bedroom" | "bathroom" | "kitchen" | "gym" | "outdoor" | "office" | "studio-dark" | "studio-warm" | "home-office" | "outdoor-night" | "minimal" | "red-mood";
  // Phase 2 V2 Fase 2 — optional, user-facing axes that give a lot of
  // variance per click. When absent, the backend falls back to the random
  // rotation pools (Fase 1). When present, the backend uses the exact
  // chosen value so the user gets reproducible control over the look.
  photographic_style?: "natural" | "golden-hour" | "studio" | "flash" | "selfie";
  skin_finish?: "natural" | "dewy" | "matte";
  // Phase 2 V2 Fase 3 — "Describilo" path. When the user writes their own
  // free-form avatar description (richer than any 7-field combo could
  // express), we carry the text here. The backend expander short-circuits
  // on this field and uses the prompt verbatim as the casting profile.
  // The 7 required fields are still set (to fallbacks) so the rest of the
  // pipeline keeps its type contract, but they're ignored when this is set.
  custom_prompt?: string;
  recommendation_id?: string;
  recommendation_role?: string;
  recommendation_label?: string;
  recommendation_fit_reason?: string;
  recommendation_preview_url?: string;
  image_provider?: string;
  image_model?: string;
  source?: string;
}

export interface VideoGenerationRequest {
  style_id: string;
  video_type_id: string;
  product_name: string;
  product_image: string;
  product_description: string;
  language: "es" | "en";
  duration: "5" | "10" | "15" | "30";
  user_prompt?: string;
  tone: "auto" | "educational" | "dramatic" | "humorous" | "urgent";
  angle?: string;
  angle_description?: string;
  problem_hint?: string;
  category_override?: string | null;
  avatar_config?: UGCAvatarConfig | null;
  // Phase 2 V2 — modeling voice-over only. Sidebar-driven audio config
  // that Step2VideoStudio forwards into the draft create request.
  voice_config?: {
    type: "tts" | "uploaded" | "none";
    voice_id?: string;
    voice_name?: string;
    uploaded_audio_url?: string;
    speed?: number;
    stability?: number;
    language_code?: string;
  } | null;
  /** Phase 2 V3 — new canonical audio-mode switch for modeling drafts.
   *  Takes precedence over voice_config in newer backends. */
  audio_mode?: "professional" | "own" | "fast";
}

export const VIDEO_STYLES: VideoStyle[] = [
  {
    // Phase 2 V2 — Modeling voice-over is the flagship card (Apr 2026).
    // Lives at position 0 in the gallery because it's the most converting
    // format (avatar + voice-over + product) and the one we're investing
    // most product polish in right now.
    id: "product-modeling-voiceover",
    nameKey: "video-style-modeling",
    descriptionKey: "video-style-modeling-desc",
    enabled: true,
    badgeKey: "new",
    estimated_time: "8-10 min",
    thumbnail: "https://fluxi.co/prod/assets/dev/assets/videos/ugc-voiceover-tutorial.mp4",
    thumbnailType: "video",
  },
  {
    id: "sassy-object",
    nameKey: "video-type-sassy-object-name",
    descriptionKey: "video-type-sassy-object-desc",
    enabled: true,
    badgeKey: "new",
    estimated_time: "10-15 min",
    thumbnail: "https://fluxi.co/dev/assets/prod/assets/ads/creatives/templates/producto-con-vida.webp",
  },
  {
    id: "animated-problem",
    nameKey: "video-type-animated-problem-name",
    descriptionKey: "video-type-animated-problem-desc",
    enabled: true,
    badgeKey: "new",
    estimated_time: "10-15 min",
    thumbnail: "https://fluxi.co/dev/assets/prod/assets/ads/creatives/templates/problema-animado.webp",
  },
  {
    id: "medical-3d",
    nameKey: "video-style-3d-anatomy",
    descriptionKey: "video-style-3d-anatomy-desc",
    enabled: false,
    badgeKey: null,
    estimated_time: "8-10 min",
    thumbnail: "https://fluxi.co/dev/assets/prod/assets/ads/creatives/templates/animated-3d.webp",
  },
  {
    id: "ugc-testimonial",
    nameKey: "video-style-ugc-testimonial",
    descriptionKey: "video-style-ugc-testimonial-desc",
    enabled: false,
    badgeKey: "new",
    estimated_time: "8-10 min",
    thumbnail: "https://fluxi.s3.amazonaws.com/prod/assets/ads/creatives/templates/ugc-testimonial.webp",
  },
  {
    // Phase 2 V1 — silent modeling. Hidden from the gallery (enabled:false)
    // but kept in the array so the style_id remains valid for legacy
    // drafts in the DB and for future resurrection if we decide to ship
    // a dedicated silent product-showcase flow.
    id: "product-modeling",
    nameKey: "video-style-modeling-silent",
    descriptionKey: "video-style-modeling-desc",
    enabled: false,
    badgeKey: null,
    estimated_time: "3-5 min",
    thumbnail: "https://fluxi.s3.amazonaws.com/prod/assets/ads/creatives/templates/modelando",
  },
  {
    id: "podcast-style",
    nameKey: "video-style-podcast",
    descriptionKey: "video-style-podcast-desc",
    enabled: false,
    badgeKey: null,
    estimated_time: "8-10 min",
    thumbnail: "https://fluxi.s3.amazonaws.com/prod/assets/prod/assets/ads/creatives/templates/podcast.webp",
  },
  {
    id: "product-demo",
    nameKey: "video-style-product-demo",
    descriptionKey: "video-style-product-demo-desc",
    enabled: false,
    badgeKey: "coming-soon",
    estimated_time: null,
  },
];

export const TONE_OPTIONS = [
  { value: "auto" },
  { value: "educational" },
  { value: "dramatic" },
  { value: "humorous" },
  { value: "urgent" },
] as const;

export const DURATION_OPTIONS = [
  { value: "5", label: "5s", subtitleKey: "video-duration-5s-subtitle", credits: 50 },
  { value: "10", label: "10s", subtitleKey: "video-duration-10s-subtitle", credits: 75 },
  { value: "15", label: "15s", subtitleKey: "video-duration-15s-subtitle", credits: 97 },
  { value: "30", label: "30s", subtitleKey: "video-duration-30s-subtitle", credits: 194 },
] as const;

// Phase 6 — UGC con Seedance 2.0 fast cuesta ~3x más que Kling V3 Pro
// porque el modelo es más caro ($0.08/s vs $0.02/s). Mantenemos el mismo
// margen cobrando más al user. Mirror exacto de
// VideoConstants.CREDITS_BY_DURATION_UGC en ecommerce-service.
export const DURATION_CREDITS_UGC: Record<string, number> = {
  "5": 75,
  "10": 110,
  "15": 145,
  "30": 290,
};

const DURATION_CREDITS_DEFAULT: Record<string, number> = {
  "5": 50,
  "10": 75,
  "15": 97,
  "30": 194,
};

// Phase 2 — Product modeling (Kling V3 Pro silent, no audio).
// Linear pricing: 10 credits per second.
export const DURATION_CREDITS_MODELING: Record<string, number> = {
  "5": 50,
  "10": 100,
  "15": 150,
};

/**
 * Devuelve el costo en créditos para una combinación style + duration.
 */
export function creditsForStyleAndDuration(styleId: string | null | undefined, duration: string | number): number {
  const key = String(duration);
  if (styleId === "ugc-testimonial") {
    return DURATION_CREDITS_UGC[key] ?? DURATION_CREDITS_UGC["30"];
  }
  if (styleId === "product-modeling") {
    return DURATION_CREDITS_MODELING[key] ?? DURATION_CREDITS_MODELING["10"];
  }
  return DURATION_CREDITS_DEFAULT[key] ?? DURATION_CREDITS_DEFAULT["30"];
}

export const AVATAR_GENDER_OPTIONS = [
  { value: "female", labelKey: "gender-female" },
  { value: "male", labelKey: "gender-male" },
] as const;

export const AVATAR_AGE_OPTIONS = [
  { value: "18-25", labelKey: "age-18-25" },
  { value: "25-35", labelKey: "age-25-35" },
  { value: "35-45", labelKey: "age-35-45" },
  { value: "45+", labelKey: "age-45+" },
] as const;

export const AVATAR_SKIN_OPTIONS = [
  { value: "light", labelKey: "skin-light", color: "#FDEBD0" },
  { value: "medium", labelKey: "skin-medium", color: "#D4A574" },
  { value: "tan", labelKey: "skin-tan", color: "#B8860B" },
  { value: "dark", labelKey: "skin-dark", color: "#5C3317" },
] as const;

export const AVATAR_HAIR_OPTIONS = [
  { value: "short-straight", labelKey: "hair-short-straight" },
  { value: "short-curly", labelKey: "hair-short-curly" },
  { value: "long-straight", labelKey: "hair-long-straight" },
  { value: "long-wavy", labelKey: "hair-long-wavy" },
  { value: "long-curly", labelKey: "hair-long-curly" },
  { value: "bun", labelKey: "hair-bun" },
  { value: "ponytail", labelKey: "hair-ponytail" },
] as const;

export const AVATAR_HAIR_COLOR_OPTIONS = [
  { value: "black", labelKey: "hair-color-black", color: "#1a1a1a" },
  { value: "brown", labelKey: "hair-color-brown", color: "#6B3A2A" },
  { value: "blonde", labelKey: "hair-color-blonde", color: "#D4A76A" },
  { value: "red", labelKey: "hair-color-red", color: "#8B2500" },
  { value: "gray", labelKey: "hair-color-gray", color: "#A0A0A0" },
] as const;

export const AVATAR_VIBE_OPTIONS = [
  { value: "casual-natural", labelKey: "vibe-casual-natural" },
  { value: "professional", labelKey: "vibe-professional" },
  { value: "sporty", labelKey: "vibe-sporty" },
  { value: "glam", labelKey: "vibe-glam" },
] as const;

export const AVATAR_SETTING_OPTIONS = [
  { value: "bedroom", labelKey: "setting-bedroom" },
  { value: "bathroom", labelKey: "setting-bathroom" },
  { value: "kitchen", labelKey: "setting-kitchen" },
  { value: "gym", labelKey: "setting-gym" },
  { value: "outdoor", labelKey: "setting-outdoor" },
  { value: "office", labelKey: "setting-office" },
] as const;

// Phase 2 V2 Fase 2 — optional axes for avatar variety. When the user
// picks one, the backend expander locks that dimension; when left blank,
// the rotating pools (Fase 1) pick a variant per generation.
export const PHOTOGRAPHIC_STYLE_OPTIONS = [
  { value: "natural", labelKey: "avatar-photo-style-natural" },
  { value: "golden-hour", labelKey: "avatar-photo-style-golden-hour" },
  { value: "studio", labelKey: "avatar-photo-style-studio" },
  { value: "flash", labelKey: "avatar-photo-style-flash" },
  { value: "selfie", labelKey: "avatar-photo-style-selfie" },
] as const;

export const SKIN_FINISH_OPTIONS = [
  { value: "natural", labelKey: "avatar-skin-finish-natural" },
  { value: "dewy", labelKey: "avatar-skin-finish-dewy" },
  { value: "matte", labelKey: "avatar-skin-finish-matte" },
] as const;

export const PODCAST_SETTING_OPTIONS = [
  { value: "studio-dark", labelKey: "setting-studio-dark" },
  { value: "studio-warm", labelKey: "setting-studio-warm" },
  { value: "home-office", labelKey: "setting-home-office" },
  { value: "outdoor-night", labelKey: "setting-outdoor-night" },
  { value: "minimal", labelKey: "setting-minimal" },
  { value: "red-mood", labelKey: "setting-red-mood" },
] as const;

export const CATEGORY_OPTIONS = [
  { value: "", labelKey: "category-auto-ai" },
  { value: "articulaciones", labelKey: "category-joints" },
  { value: "digestivo", labelKey: "category-digestive" },
  { value: "cardiovascular", labelKey: "category-cardiovascular" },
  { value: "piel-skincare", labelKey: "category-skincare" },
  { value: "cabello", labelKey: "category-hair" },
  { value: "energia", labelKey: "category-energy" },
  { value: "sueno-estres", labelKey: "category-sleep-stress" },
  { value: "peso", labelKey: "category-weight" },
  { value: "dolor-inflamacion", labelKey: "category-pain-inflammation" },
  { value: "vision", labelKey: "category-vision" },
  { value: "inmunidad", labelKey: "category-immunity" },
  { value: "musculo-fitness", labelKey: "category-muscle-fitness" },
  { value: "dental", labelKey: "category-dental" },
  { value: "higado-detox", labelKey: "category-liver-detox" },
  { value: "huesos", labelKey: "category-bones" },
  { value: "cerebro-cognitivo", labelKey: "category-brain-cognitive" },
] as const;
